/**
 * Router React components
 *
 * Provides Router, Link, LinkButton, and Navigate components that integrate with
 * the signal-based router state management system.
 */

import type { VariantProps } from 'class-variance-authority';

import type { RouteParams, RoutePath } from '#routes.js';

import React from 'react';

import { shouldLetBrowserHandleClick } from '@elara/client-core/lib/route-utils';
import { cn } from '@elara/client-core/lib/utils';

import { useRouterState } from '#states/hooks.js';
import { buttonVariants } from '#ui/button-variants.js';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '#ui/tooltip.js';

interface RouterProps {
  children: React.ReactNode;
}

/**
 * Type-safe Link component for navigation with route parameter validation
 * This is the default and only Link component - always use this for navigation
 */
export function Link<T extends RoutePath>({
  to,
  params,
  children,
  replace = false,
  className,
  style,
  onClick,
}: {
  to: T;
  params?: RouteParams[T] extends never ? never : RouteParams[T];
  children?: React.ReactNode;
  replace?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent) => void;
}): React.JSX.Element {
  const routerState = useRouterState();

  const handleClick = (e: React.MouseEvent) => {
    // Call custom onClick handler if provided
    if (onClick) {
      onClick(e);
    }

    // Let browser handle modifier clicks (Cmd+Click, Ctrl+Click, etc.)
    if (shouldLetBrowserHandleClick(e)) {
      return;
    }

    e.preventDefault();

    // Use type-safe navigation
    if (params) {
      routerState.navigate(routerState.buildPathWithParams(to, params), { replace });
    } else {
      routerState.navigate(to, { replace });
    }
  };

  // Build href for accessibility
  const href = params ? routerState.buildPathWithParams(to, params) : to;

  return (
    <a href={href} onClick={handleClick} className={className} style={style}>
      {children}
    </a>
  );
}

/**
 * Type-safe LinkButton component for navigation with button styling
 * Renders as an anchor tag for proper link semantics while looking like a button.
 * Supports Command/Ctrl+Click to open in new tab.
 */
export function LinkButton<T extends RoutePath>({
  to,
  params,
  children,
  replace = false,
  className,
  variant,
  size,
  onClick,
  disabled,
  tooltip,
  tooltipPosition,
}: {
  to: T;
  params?: RouteParams[T] extends never ? never : RouteParams[T];
  children: React.ReactNode;
  replace?: boolean;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
  tooltip?: string;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
} & VariantProps<typeof buttonVariants>): React.JSX.Element {
  const routerState = useRouterState();

  const handleClick = (e: React.MouseEvent) => {
    // Call custom onClick handler if provided
    if (onClick) {
      onClick(e);
    }

    // Don't navigate if disabled
    if (disabled) {
      e.preventDefault();
      return;
    }

    // Let browser handle modifier clicks (Cmd+Click, Ctrl+Click, etc.)
    if (shouldLetBrowserHandleClick(e)) {
      return;
    }

    e.preventDefault();

    // Use type-safe navigation
    if (params) {
      routerState.navigate(routerState.buildPathWithParams(to, params), { replace });
    } else {
      routerState.navigate(to, { replace });
    }
  };

  // Build href for accessibility
  const href = params ? routerState.buildPathWithParams(to, params) : to;

  const anchor = (
    <a
      href={href}
      onClick={handleClick}
      className={cn(buttonVariants({ variant, size, className }), disabled && 'pointer-events-none opacity-50')}
      aria-disabled={disabled}
    >
      {children}
    </a>
  );

  if (tooltip) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{anchor}</TooltipTrigger>
          <TooltipContent side={tooltipPosition || 'top'} align="center" sideOffset={8}>
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return anchor;
}

/**
 * Type-safe Navigate component for programmatic navigation
 * Renders nothing but triggers navigation on mount
 */
export function Navigate<T extends RoutePath>({
  to,
  params,
  replace = false,
}: {
  to: T;
  params?: RouteParams[T] extends never ? never : RouteParams[T];
  replace?: boolean;
}): null {
  const routerState = useRouterState();

  React.useEffect(() => {
    if (params) {
      routerState.navigate(routerState.buildPathWithParams(to, params), { replace });
    } else {
      routerState.navigate(to, { replace });
    }
  }, [routerState, to, params, replace]);

  return null;
}
